/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';
import { useHover } from 'react-native-web-hooks';

import { scale } from '@components/frame';

import ColorWrapper, { checkColor } from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import Image from '@components/common/Image';
import TouchableElement from '@components/common/touchableElement';
import ItemHistoryTime from '@components/common/ItemHistoryTime';
import { KEYS_TAGS } from '@components/imageHandling/common';

import { SmallLoading } from '@components/loading';

import { isPhone, ImageUpload } from '@helpers';
import { IMAGE_UPLOAD_HEADER_TEXT } from '@v2/pages/common/constants/base.constants';

const ItemName = ({ shortName, C, selected }) => (
  <View style={{ flexDirection: 'row', alignContent: 'center', alignItems: 'center', marginTop: scale(-2), paddingRight: scale(isPhone ? 35 : 0) }}>
    <Text
      style={[{ marginLeft: scale(5.5), color: C(selected ? 'Label/Light/AlwaysLight Primary' : 'Label / Light / Primary') }, font.Headline]}
      numberOfLines={1}
    >
      {shortName}
    </Text>
  </View>
);

const ItemImg = ({ C, ncFileId, ncFileName, imageHash, imageData = {}, selected, isProcessing, generateImageURL }) => {
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageLink, setImageLink] = useState(null);

  useEffect(() => {
    (async () => {
      if (imageHash && imageData) {
        const imageLink = await generateImageURL({
          size: '_1x',
          cropped: imageData?.isCropped,
          item: { imageHash, extensionDerivative: imageData?.extensionDerivative },
          page: imageData?.pageNumber,
        });
        setImageLink(imageLink);
      }
    })();
  }, [imageHash]);

  const possibleAddImage = ncFileId && !imageHash && !uploadedImage && !isProcessing;

  const uploadImageFunction = () => {
    if (loading || !possibleAddImage) {
      return;
    }

    ImageUpload({
      headerText: IMAGE_UPLOAD_HEADER_TEXT.AddToNcFile,
      headerDescription: ncFileName,
      uploadStart: () => setLoading(true),
      uploadEnd: (img) => {
        setLoading(false);
        setUploadedImage(img);
      },
      tags: [
        {
          scopeId: KEYS_TAGS['Clamping'],
          scopeReferenceId: ncFileId,
          isFavorite: true,
        },
      ],
    });
  };
  const ClickWrapper = ({ children }) =>
    possibleAddImage ? (
      <TouchableElement onPress={uploadImageFunction}>{({ boxStyle }) => <View style={[boxStyle]}>{children}</View>}</TouchableElement>
    ) : (
      <View>{children}</View>
    );

  return (
    <ClickWrapper>
      <View
        style={[
          {
            width: scale(90),
            height: scale(60),
            position: 'relative',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
          },
        ]}
      >
        <View
          style={{
            width: scale(90),
            height: scale(60),
            backgroundColor: C('Overlay/Light/Dim very light'),
            opacity: 0.5,
            position: 'absolute',
          }}
        />
        {isProcessing && (
          <View
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              zIndex: 10000,
              backgroundColor: C('Background/Light/System'),
            }}
          >
            <View
              style={{
                backgroundColor: C('Picture/Light/Overlay'),
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Svg icon={'pdf'} width={scale(23)} height={scale(29)} color="Label/Light/Quaternary" />
              <Text style={[font.Footnote, { color: C('Label/Light/Quaternary') }]}>{i18n.t('Processing...')}</Text>
            </View>
          </View>
        )}
        {false ? (
          <SmallLoading />
        ) : possibleAddImage ? (
          <TouchableElement onPress={uploadImageFunction}>
            {({ boxStyle, hovered }) => (
              <View
                style={[
                  boxStyle,
                  {
                    backgroundColor: hovered ? C(selected ? 'Button/Light/Secondary hover' : 'Button/Light/Secondary hover') : 'transparent',
                    borderRadius: scale(4),
                    padding: scale(8),
                  },
                ]}
              >
                <Svg
                  icon="addPicture"
                  color={selected ? (hovered ? 'Button/Light/Primary Text' : 'Label/Light/Always Light Tertiary') : 'Button/Light/Secondary Text'}
                  width={scale(45)}
                  height={scale(34)}
                  key={'image-icon-' + selected}
                />
              </View>
            )}
          </TouchableElement>
        ) : (
          <Image source={uploadedImage ? uploadedImage : { uri: imageLink || ' ' }} style={{ width: '100%', height: '100%' }} resizeMode="contain" />
        )}
        {!possibleAddImage && (
          <View
            style={{
              width: scale(90),
              height: scale(60),
              backgroundColor: C('Overlay/Light/Dim light'),
              opacity: 0.5,
              borderRadius: scale(2),
              position: 'absolute',
            }}
          />
        )}
      </View>
    </ClickWrapper>
  );
};

const Item = ({ item, setSelectedNCFile, selected: s, C, nextSelected, generateImageURL, listWidth, roundTop, roundBottom }) => {
  const ref = useRef(null);
  const [pressed, setPressed] = useState(false);
  const hovered = useHover(ref);

  const pressItem = (item) => {
    setSelectedNCFile({ machineStatus: { ...item, ncFile: item.ncFileName } });
  };

  const selected = s && !isPhone;
  const displayOverlay = selected || pressed || hovered;

  const pressHoverColor = (c) => {
    if (pressed) {
      return checkColor(c, 'pressed');
    }
    if (hovered) {
      return checkColor(c, 'hover');
    }
    return c;
  };

  return (
    <TouchableOpacity
      style={[
        {
          paddingLeft: scale(isPhone ? 18 : displayOverlay ? 16 : 26),
          paddingRight: scale(isPhone ? 0 : displayOverlay ? 16 : 26),
          width: '100%',
          height: scale(80),
          position: 'relative',
          zIndex: 100000,
        },
        isPhone && {
          backgroundColor: C(pressHoverColor('Background/Light/System')),
        },
        roundTop && {
          borderTopLeftRadius: scale(10),
          borderTopRightRadius: scale(10),
        },
        roundBottom && {
          borderBottomLeftRadius: scale(10),
          borderBottomRightRadius: scale(10),
        },
      ]}
      onPress={() => {
        if (item.statisticOnly) {
          return;
        }
        pressItem(item);
      }}
      activeOpacity={1}
      ref={ref}
      onPressIn={() => {
        setPressed(true);
      }}
      onPressOut={() => {
        setPressed(false);
      }}
    >
      <View
        style={[
          {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            paddingBottom: scale(10),
            paddingTop: scale(10),
            flexDirection: 'row',
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: C('Hairline/Light/No trans'),
            overflow: 'hidden',
          },
          selected && {
            backgroundColor: C('Selected/Light/List item'),
            borderBottomWidth: 0,
            borderRadius: scale(10),
            paddingLeft: scale(10),
            paddingRight: scale(10),
          },
          nextSelected && !isPhone && { borderBottomWidth: 0 },
          (pressed || hovered) &&
            !selected &&
            !isPhone && {
              backgroundColor: C(pressHoverColor('Background/Light/Field')),
              borderRadius: scale(10),
              paddingLeft: scale(isPhone ? 0 : 10),
              paddingRight: scale(isPhone ? 0 : 10),
            },
        ]}
      >
        <ItemImg
          {...{
            C,
            imageData: item.imageData,
            imageHash: item.imageHash,
            ncFileId: item.ncFileId,
            ncFileName: item.ncFileName ?? item.ncFile,
            isProcessing: item.isProcessing,
            generateImageURL,
            selected,
          }}
        />
        <View
          style={{
            height: '100%',
            marginLeft: scale(5.5, false),
            flex: 1,
          }}
        >
          <ItemName
            {...{
              shortName: item.ncFileName,
              C,
              selected,
            }}
          />
          <ItemHistoryTime
            {...{
              item: {
                machineStatistics: { ...item, totalLoaded: true },
                maxValueProductive: item.maxValueProductive,
                maxValueActive: item.maxValueActive,
              },
              C,
              selected,
              listWidth,
            }}
          />
        </View>
      </View>
    </TouchableOpacity>
  );
};

const compareProps = (prev, next) => {
  return (
    prev.C.scheme === next.C.scheme &&
    prev.selected === next.selected &&
    prev.nextSelected === next.nextSelected &&
    prev.item?.ncFileId === next.item?.ncFileId &&
    prev.item?.ncFileName === next.item?.ncFileName &&
    prev.item?.ncFile === next.item?.ncFile
  );
};

export default ColorWrapper(React.memo(Item, compareProps));
